import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { formatMarketName } from "../utils/formatMarketName";
import { Link } from "react-router-dom";

const cardStyle = {
  border: "1px solid rgba(148,163,184,0.35)",
  background: "rgba(2,8,20,0.9)",
  borderRadius: 12,
  padding: 12,
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const fmtNum = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
};

const fmtPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
};

const fmtSignedNum = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n > 0) return `+${n.toFixed(digits)}`;
  return n.toFixed(digits);
};

const roiColor = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "#94a3b8";
  if (n > 0) return "#34d399";
  if (n < 0) return "#f87171";
  return "#cbd5e1";
};

const settledValue = (row) =>
  Number(row?.settled_scored || 0);

async function copyJson(value) {
  const text = JSON.stringify(value ?? {}, null, 2);
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // noop: keep this utility non-blocking
  }
}

const sanitizeDaysBack = (value) => {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : 90;
};

const sanitizeMinEdge = (value) => {
  const normalized = String(value).replace(",", ".").trim();
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildEdgePerformanceParams = ({
  daysBack,
  minEdge,
  latestOnly,
  excludeModelBlacklist,
  excludeBookmakersCsv,
  excludeAsianHandicap,
  performanceSport,
}) => {
  const params = {
    source: "team_form",
    days_back: sanitizeDaysBack(daysBack),
    min_edge: sanitizeMinEdge(minEdge),
    latest_only: latestOnly ? 1 : 0,
    exclude_model_blacklist: excludeModelBlacklist ? 1 : 0,
    resolve_halftime: 1,
    sport: performanceSport || "all",
  };

  const trimmedBookmakers = String(excludeBookmakersCsv || "").trim();
  if (trimmedBookmakers) params.exclude_bookmakers = trimmedBookmakers;
  if (excludeAsianHandicap) params.exclude_markets = "AH_*,SPREAD_*";

  return params;
};

export default function AdminModelHealthPanel() {
  const [daysBack, setDaysBack] = useState(30);
  const [minEdge, setMinEdge] = useState(0.03);
  const [latestOnly, setLatestOnly] = useState(true);
  const [excludeModelBlacklist, setExcludeModelBlacklist] = useState(true);
  const [excludeBookmakersCsv, setExcludeBookmakersCsv] = useState("");
  const [excludeAsianHandicap, setExcludeAsianHandicap] = useState(true);
  const [performanceSport, setPerformanceSport] = useState("all");

  const [perfLoading, setPerfLoading] = useState(false);
  const [perfError, setPerfError] = useState("");
  const [performanceData, setPerformanceData] = useState(null);
  const [perfUpdatedAt, setPerfUpdatedAt] = useState(null);
  const [perfRequestParams, setPerfRequestParams] = useState(null);
  const [perfRequestUrl, setPerfRequestUrl] = useState("");
  const perfRequestSeqRef = useRef(0);

  const [recalLoading, setRecalLoading] = useState(false);
  const [recalError, setRecalError] = useState("");
  const [recalResponse, setRecalResponse] = useState(null);
  const [recalUpdatedAt, setRecalUpdatedAt] = useState(null);

  const [recomputeLoading, setRecomputeLoading] = useState(false);
  const [recomputeError, setRecomputeError] = useState("");
  const [recomputeResponse, setRecomputeResponse] = useState(null);
  const [recomputeUpdatedAt, setRecomputeUpdatedAt] = useState(null);

  const [signalDaysBack, setSignalDaysBack] = useState(30);
  const [signalSport, setSignalSport] = useState("football");
  const [signalStrategy, setSignalStrategy] = useState("all");
  const [signalStrategies, setSignalStrategies] = useState([]);
  const [signalPerfLoading, setSignalPerfLoading] = useState(false);
  const [signalPerfError, setSignalPerfError] = useState("");
  const [signalPerfData, setSignalPerfData] = useState(null);
  const [signalPerfUpdatedAt, setSignalPerfUpdatedAt] = useState(null);

  const [notifyPremiumOnScan, setNotifyPremiumOnScan] = useState(true);
  const [includeAllLeaguesOnScan, setIncludeAllLeaguesOnScan] = useState(false);
  const [scanSignalsLoading, setScanSignalsLoading] = useState(false);
  const [scanSignalsResponse, setScanSignalsResponse] = useState(null);
  const [scanSignalsError, setScanSignalsError] = useState("");
  const [monitorSignalsLoading, setMonitorSignalsLoading] = useState(false);
  const [monitorSignalsResponse, setMonitorSignalsResponse] = useState(null);
  const [monitorSignalsError, setMonitorSignalsError] = useState("");
  const [signalsActionNotice, setSignalsActionNotice] = useState(null);

  const fetchPerformance = useCallback(async (overrides = {}) => {
    const params = buildEdgePerformanceParams({
      daysBack,
      minEdge,
      latestOnly,
      excludeModelBlacklist,
      excludeBookmakersCsv,
      excludeAsianHandicap,
      performanceSport,
      ...overrides,
    });
    const requestSeq = perfRequestSeqRef.current + 1;
    perfRequestSeqRef.current = requestSeq;
    const queryString = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    ).toString();

    setPerfRequestParams(params);
    setPerfRequestUrl(`/admin/edge-performance?${queryString}`);
    setPerfLoading(true);
    setPerfError("");

    if (process.env.NODE_ENV !== "production") {
      console.log("[AdminModelHealthPanel] edge-performance request", {
        url: `/admin/edge-performance?${queryString}`,
        params,
      });
    }

    try {
      const { data } = await api.get("/admin/edge-performance", {
        params: {
          ...params,
          _ts: Date.now(),
        },
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });
      if (requestSeq !== perfRequestSeqRef.current) return;
      setPerformanceData(data || null);
      setPerfUpdatedAt(new Date());
    } catch (err) {
      if (requestSeq !== perfRequestSeqRef.current) return;
      setPerfError(err?.response?.data?.detail || err?.message || "Could not load edge performance.");
      setPerformanceData(null);
    } finally {
      if (requestSeq === perfRequestSeqRef.current) {
        setPerfLoading(false);
      }
    }
  }, [daysBack, minEdge, latestOnly, excludeModelBlacklist, excludeBookmakersCsv, excludeAsianHandicap, performanceSport]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get("/api/signals/strategies");
        if (dead) return;
        const rows = Array.isArray(data?.strategies) ? data.strategies : Array.isArray(data) ? data : [];
        setSignalStrategies(rows);
      } catch {
        if (!dead) setSignalStrategies([]);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  const fetchSignalsPerformance = async () => {
    setSignalPerfLoading(true);
    setSignalPerfError("");
    try {
      const params = {
        days_back: signalDaysBack,
        sport: signalSport,
      };
      if (signalStrategy && signalStrategy !== "all") params.strategy_key = signalStrategy;
      const { data } = await api.get("/api/signals/performance", { params });
      setSignalPerfData(data || null);
      setSignalPerfUpdatedAt(new Date());
    } catch (err) {
      setSignalPerfError(err?.response?.data?.detail || err?.message || "Could not load signals performance.");
      setSignalPerfData(null);
    } finally {
      setSignalPerfLoading(false);
    }
  };

  useEffect(() => {
    fetchSignalsPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalDaysBack, signalSport, signalStrategy]);

  const overall = performanceData?.overall || {};
  const byMarketRows = useMemo(() => {
    const rows = Array.isArray(performanceData?.by_market) ? performanceData.by_market : [];
    return [...rows].sort((a, b) => settledValue(b) - settledValue(a));
  }, [performanceData]);
  const bySportRows = useMemo(() => {
    const rows = Array.isArray(performanceData?.by_sport) ? performanceData.by_sport : [];
    return [...rows].sort((a, b) => settledValue(b) - settledValue(a));
  }, [performanceData]);
  const byBookmakerRows = useMemo(() => {
    const rows = Array.isArray(performanceData?.by_bookmaker) ? performanceData.by_bookmaker : [];
    return [...rows].sort((a, b) => settledValue(b) - settledValue(a));
  }, [performanceData]);
  const byEdgeBandRows = useMemo(
    () => (Array.isArray(performanceData?.by_edge_band) ? performanceData.by_edge_band : []),
    [performanceData]
  );

  const doRecalibrate = async () => {
    setRecalLoading(true);
    setRecalError("");
    setRecalResponse(null);
    try {
      const { data } = await api.post("/admin/calibrate-team-form-now", null, {
        params: {
          days_back: 90,
          book: "bet365",
          scope: "global",
          min_samples: 20,
        },
      });
      setRecalResponse(data || null);
      setRecalUpdatedAt(new Date());
      await fetchPerformance();
    } catch (err) {
      setRecalError(err?.response?.data?.detail || err?.message || "Recalibration failed.");
    } finally {
      setRecalLoading(false);
    }
  };

  const doRecompute = async () => {
    setRecomputeLoading(true);
    setRecomputeError("");
    setRecomputeResponse(null);
    try {
      const { data } = await api.post("/admin/recompute-edges", null, {
        params: {
          source: "team_form",
          hours_ahead: 96,
          min_edge: 0.0,
          ensure_baseline: 1,
        },
      });
      setRecomputeResponse(data || null);
      setRecomputeUpdatedAt(new Date());
      await fetchPerformance();
    } catch (err) {
      setRecomputeError(err?.response?.data?.detail || err?.message || "Recompute failed.");
    } finally {
      setRecomputeLoading(false);
    }
  };

  const kpis = [
    {
      label: "Edges considered",
      value: firstDefined(overall.edges_considered, "—"),
    },
    {
      label: "Settled / scored",
      value: firstDefined(overall.settled_scored, "—"),
    },
    {
      label: "Hit rate",
      value: fmtPct(overall.hit_rate_pct),
      tone: roiColor(overall.hit_rate_pct),
    },
    {
      label: "ROI",
      value: fmtPct(overall.roi_pct),
      tone: roiColor(overall.roi_pct),
    },
    {
      label: "Flat P/L units",
      value: fmtSignedNum(overall.flat_pl_units, 2),
      tone: roiColor(overall.flat_pl_units),
    },
    {
      label: "Average CLV",
      value: fmtPct(overall.avg_clv_price_pct),
      tone: roiColor(overall.avg_clv_price_pct),
    },
    {
      label: "Beat close",
      value: fmtPct(overall.positive_clv_rate_pct),
      tone: roiColor(Number(overall.positive_clv_rate_pct) - 50),
    },
    {
      label: "CLV samples",
      value: firstDefined(overall.clv_samples, "—"),
    },
    {
      label: "Pushes / unscored",
      value: firstDefined(overall.pushes_or_unscored, "—"),
    },
    {
      label: "Calibration gap",
      value: fmtPct(overall.calibration_gap_pp),
      tone: roiColor(-Math.abs(Number(overall.calibration_gap_pp))),
    },
    {
      label: "Brier score",
      value: fmtNum(overall.brier_score, 4),
    },
  ];

  const recalSummary = recalResponse?.summary || {};
  const recalResults = Array.isArray(recalResponse?.results) ? recalResponse.results : [];
  const signalRows = useMemo(() => {
    const fromRows =
      Array.isArray(signalPerfData?.rows)
        ? signalPerfData.rows
        : Array.isArray(signalPerfData?.by_strategy)
        ? signalPerfData.by_strategy
        : Array.isArray(signalPerfData?.strategies)
        ? signalPerfData.strategies
        : Array.isArray(signalPerfData)
        ? signalPerfData
        : [];
    return [...fromRows].sort(
      (a, b) =>
        Number(b?.signals_settled ?? b?.settled ?? 0) -
        Number(a?.signals_settled ?? a?.settled ?? 0)
    );
  }, [signalPerfData]);

  const signalSummary = useMemo(() => {
    const fromApi = signalPerfData?.summary || signalPerfData?.overall;
    if (fromApi && typeof fromApi === "object") return fromApi;
    const totals = signalRows.reduce(
      (acc, row) => {
        const strategies = 1;
        const total = Number(row?.signals_total ?? row?.signals ?? 0) || 0;
        const settled = Number(row?.signals_settled ?? row?.settled ?? 0) || 0;
        const won = Number(row?.won ?? row?.signals_won ?? 0) || 0;
        const closedPl = Number(row?.position_pl_units ?? row?.positions_pl_units ?? row?.closed_position_pl ?? 0) || 0;
        acc.strategies += strategies;
        acc.total += total;
        acc.settled += settled;
        acc.won += won;
        acc.closedPl += closedPl;
        return acc;
      },
      { strategies: 0, total: 0, settled: 0, won: 0, closedPl: 0 }
    );
    return {
      total_strategies: totals.strategies,
      total_signals: totals.total,
      total_settled: totals.settled,
      weighted_hit_rate_pct: totals.settled ? (totals.won / totals.settled) * 100 : null,
      total_closed_position_pl: totals.closedPl,
    };
  }, [signalPerfData, signalRows]);

  const runScanSignals = async () => {
    setScanSignalsLoading(true);
    setScanSignalsError("");
    try {
      const params = {
        strategy_key: signalStrategy && signalStrategy !== "all" ? signalStrategy : "__all_active__",
        notify_premium: notifyPremiumOnScan ? 1 : 0,
        include_all_leagues: includeAllLeaguesOnScan ? 1 : 0,
      };
      const { data } = await api.post("/api/signals/scan-live", null, { params });
      setScanSignalsResponse(data || {});
      setSignalsActionNotice({ ok: true, text: "Scan Live Signals completed." });
      await fetchSignalsPerformance();
    } catch (err) {
      setScanSignalsError(err?.response?.data?.detail || err?.message || "Signal scan failed.");
      setSignalsActionNotice({ ok: false, text: "Scan Live Signals failed." });
    } finally {
      setScanSignalsLoading(false);
    }
  };

  const runMonitorSignals = async () => {
    setMonitorSignalsLoading(true);
    setMonitorSignalsError("");
    try {
      const { data } = await api.post("/api/signals/monitor-exits");
      setMonitorSignalsResponse(data || {});
      setSignalsActionNotice({ ok: true, text: "Run Exit Monitor completed." });
      await fetchSignalsPerformance();
    } catch (err) {
      setMonitorSignalsError(err?.response?.data?.detail || err?.message || "Exit monitor failed.");
      setSignalsActionNotice({ ok: false, text: "Run Exit Monitor failed." });
    } finally {
      setMonitorSignalsLoading(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Edge Performance</h3>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Last updated: {perfUpdatedAt ? perfUpdatedAt.toLocaleTimeString() : "—"}
          </div>
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ fontSize: 12 }}>
            Days back{" "}
            <select value={daysBack} onChange={(e) => setDaysBack(Number(e.target.value))}>
              {[7, 14, 30, 60, 90].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>
            Sport{" "}
            <select value={performanceSport} onChange={(e) => setPerformanceSport(e.target.value)}>
              <option value="all">All</option>
              <option value="football">Football</option>
              <option value="baseball">Baseball</option>
              <option value="basketball">Basketball</option>
              <option value="ice_hockey">Ice hockey</option>
            </select>
          </label>
          <label style={{ fontSize: 12 }}>
            Min edge{" "}
            <select value={String(minEdge)} onChange={(e) => setMinEdge(Number(e.target.value))}>
              {[0, 0.01, 0.03, 0.05].map((n) => (
                <option key={String(n)} value={String(n)}>{n}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={latestOnly}
              onChange={(e) => setLatestOnly(e.target.checked)}
            />
            Latest only
          </label>
          <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={excludeModelBlacklist}
              onChange={(e) => setExcludeModelBlacklist(e.target.checked)}
            />
            Exclude bad feeds
          </label>
          <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={excludeAsianHandicap}
              onChange={(e) => setExcludeAsianHandicap(e.target.checked)}
            />
            Exclude legacy AH / spreads
          </label>
          <label style={{ fontSize: 12 }}>
            Exclude bookmakers (CSV){" "}
            <input
              value={excludeBookmakersCsv}
              onChange={(e) => setExcludeBookmakersCsv(e.target.value)}
              placeholder="betfair,unibet"
              style={{ minWidth: 170 }}
            />
          </label>
          <button
            type="button"
            onClick={fetchPerformance}
            disabled={perfLoading}
            style={{ padding: "6px 10px", borderRadius: 8, cursor: perfLoading ? "default" : "pointer" }}
          >
            {perfLoading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            onClick={() => copyJson(performanceData)}
            style={{ padding: "6px 10px", borderRadius: 8 }}
          >
            Copy JSON
          </button>
        </div>

        {perfError ? (
          <div style={{ marginTop: 10, color: "#fca5a5", fontSize: 13 }}>{perfError}</div>
        ) : null}

        <div
          style={{
            marginTop: 10,
            border: "1px solid rgba(148,163,184,0.25)",
            borderRadius: 10,
            padding: 10,
            background: "rgba(15,23,42,0.35)",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#cbd5e1" }}>Request Debug</div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8", wordBreak: "break-all" }}>
            {perfRequestUrl || "/admin/edge-performance"}
          </div>
          <pre
            style={{
              margin: "8px 0 0",
              fontSize: 12,
              color: "#cbd5e1",
              whiteSpace: "pre-wrap",
            }}
          >
            {JSON.stringify(performanceData?.params ?? perfRequestParams ?? {}, null, 2)}
          </pre>
        </div>

        <div style={{ marginTop: 12, display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          {kpis.map((kpi) => (
            <div key={kpi.label} style={{ border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>{kpi.label}</div>
              <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700, color: kpi.tone || "#e5e7eb" }}>{kpi.value}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#e5e7eb", marginBottom: 8 }}>
            Performance by sport
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Sport", "Settled", "Hit rate %", "ROI %", "Flat P/L", "Avg CLV %", "CLV N", "Unscored"].map((h) => (
                    <th key={h} style={{ textAlign: h === "Sport" ? "left" : "right", padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.3)", color: "#cbd5e1" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bySportRows.map((row) => (
                  <tr key={row.sport || "unknown"}>
                    <td style={{ padding: "8px 6px", textTransform: "capitalize", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{String(row.sport || "unknown").replaceAll("_", " ")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{settledValue(row)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.hit_rate_pct)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", color: roiColor(row.roi_pct), borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.roi_pct)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", color: roiColor(row.flat_pl_units), borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtSignedNum(row.flat_pl_units, 2)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", color: roiColor(row.avg_clv_price_pct), borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.avg_clv_price_pct)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.clv_samples, "—")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.pushes_or_unscored, "—")}</td>
                  </tr>
                ))}
                {!perfLoading && !bySportRows.length ? (
                  <tr><td colSpan={8} style={{ padding: 10, color: "#94a3b8" }}>No sport rows returned.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {[
          { title: "Performance by edge band", rows: byEdgeBandRows, key: "edge_band", label: "Edge band" },
          { title: "Performance by bookmaker", rows: byBookmakerRows, key: "bookmaker", label: "Bookmaker" },
        ].map((section) => (
          <div key={section.title} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#e5e7eb", marginBottom: 8 }}>
              {section.title}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {[section.label, "Settled", "ROI %", "Model %", "Actual %", "Gap pp", "Brier", "Avg CLV %"].map((h) => (
                      <th key={h} style={{ textAlign: h === section.label ? "left" : "right", padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.3)", color: "#cbd5e1" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row) => (
                    <tr key={row[section.key] || "unknown"}>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)", textTransform: "capitalize" }}>{row[section.key] || "unknown"}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{settledValue(row)}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", color: roiColor(row.roi_pct), borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.roi_pct)}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.avg_model_prob_pct)}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.actual_win_rate_pct)}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", color: roiColor(-Math.abs(Number(row.calibration_gap_pp))), borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtSignedNum(row.calibration_gap_pp, 2)}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtNum(row.brier_score, 4)}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right", color: roiColor(row.avg_clv_price_pct), borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.avg_clv_price_pct)}</td>
                    </tr>
                  ))}
                  {!perfLoading && !section.rows.length ? (
                    <tr><td colSpan={8} style={{ padding: 10, color: "#94a3b8" }}>Deploy the latest API to populate this breakdown.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div style={{ marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Market", "Settled", "Wins", "Losses", "Hit rate %", "ROI %", "Avg CLV %", "Beat close %", "CLV N", "Flat P/L", "Edges considered", "Pushes/unscored"].map((h) => (
                  <th key={h} style={{ textAlign: h === "Market" ? "left" : "right", padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.3)", color: "#cbd5e1" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byMarketRows.map((row, idx) => {
                const roi = row.roi_pct;
                const flatPl = row.flat_pl_units;
                return (
                  <tr key={`${row.market || "m"}-${idx}`}>
                    <td style={{ padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{formatMarketName(row.market || "—")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{settledValue(row)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.wins, "—")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.losses, "—")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.hit_rate_pct)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)", color: roiColor(roi) }}>{fmtPct(roi)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)", color: roiColor(row.avg_clv_price_pct) }}>{fmtPct(row.avg_clv_price_pct)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(row.positive_clv_rate_pct)}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.clv_samples, "—")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)", color: roiColor(flatPl) }}>
                      {fmtSignedNum(flatPl, 2)}
                    </td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.edges_considered, "—")}</td>
                    <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.pushes_or_unscored, "—")}</td>
                  </tr>
                );
              })}
              {!perfLoading && !byMarketRows.length ? (
                <tr>
                  <td colSpan={12} style={{ padding: 10, color: "#94a3b8" }}>No market rows returned.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Signals Performance</h3>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Last updated: {signalPerfUpdatedAt ? signalPerfUpdatedAt.toLocaleTimeString() : "—"}
          </div>
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ fontSize: 12 }}>
            Days back{" "}
            <select value={signalDaysBack} onChange={(e) => setSignalDaysBack(Number(e.target.value))}>
              {[7, 14, 30, 60, 90].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>
            Sport{" "}
            <select value={signalSport} onChange={(e) => setSignalSport(e.target.value)}>
              <option value="football">football</option>
              <option value="all">all</option>
            </select>
          </label>
          <label style={{ fontSize: 12 }}>
            Strategy{" "}
            <select value={signalStrategy} onChange={(e) => setSignalStrategy(e.target.value)}>
              <option value="all">All</option>
              {signalStrategies.map((row, i) => {
                const key = row?.key || row?.strategy_key || row?.name || `s-${i}`;
                const label = row?.name || row?.label || row?.key || row?.strategy_key || key;
                return (
                  <option key={key} value={key}>{label}</option>
                );
              })}
            </select>
          </label>
          <button
            type="button"
            onClick={fetchSignalsPerformance}
            disabled={signalPerfLoading}
            style={{ padding: "6px 10px", borderRadius: 8 }}
          >
            {signalPerfLoading ? "Refreshing…" : "Refresh"}
          </button>
          <Link
            to="/signals?tab=backtests"
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid rgba(148,163,184,0.45)",
              color: "#e5e7eb",
              textDecoration: "none",
              fontSize: 13,
            }}
          >
            View backtests
          </Link>
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={notifyPremiumOnScan}
              onChange={(e) => setNotifyPremiumOnScan(e.target.checked)}
            />
            Notify premium users on new signals
          </label>
          <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={includeAllLeaguesOnScan}
              onChange={(e) => setIncludeAllLeaguesOnScan(e.target.checked)}
            />
            Include all leagues
          </label>
          <button
            type="button"
            onClick={runScanSignals}
            disabled={scanSignalsLoading}
            style={{ padding: "6px 10px", borderRadius: 8 }}
          >
            {scanSignalsLoading ? "Scanning…" : "Scan Live Signals"}
          </button>
          <button
            type="button"
            onClick={runMonitorSignals}
            disabled={monitorSignalsLoading}
            style={{ padding: "6px 10px", borderRadius: 8 }}
          >
            {monitorSignalsLoading ? "Running…" : "Run Exit Monitor"}
          </button>
        </div>

        {signalsActionNotice ? (
          <div
            style={{
              marginTop: 10,
              fontSize: 13,
              borderRadius: 8,
              padding: "6px 10px",
              border: signalsActionNotice.ok
                ? "1px solid rgba(52,211,153,0.45)"
                : "1px solid rgba(248,113,113,0.45)",
              background: signalsActionNotice.ok
                ? "rgba(5,150,105,0.16)"
                : "rgba(153,27,27,0.2)",
              color: signalsActionNotice.ok ? "#6ee7b7" : "#fca5a5",
            }}
          >
            {signalsActionNotice.text}
          </div>
        ) : null}

        {scanSignalsError ? <div style={{ marginTop: 10, color: "#fca5a5", fontSize: 13 }}>{scanSignalsError}</div> : null}
        {monitorSignalsError ? <div style={{ marginTop: 6, color: "#fca5a5", fontSize: 13 }}>{monitorSignalsError}</div> : null}

        {(scanSignalsResponse || monitorSignalsResponse) && (
          <div style={{ marginTop: 10, fontSize: 13, color: "#cbd5e1", display: "grid", gap: 4 }}>
            <div>
              scan-live: evaluated={firstDefined(scanSignalsResponse?.evaluated, "—")}, triggered={firstDefined(scanSignalsResponse?.triggered, "—")}, newly_created={firstDefined(scanSignalsResponse?.newly_created, "—")}, alerts_sent={firstDefined(scanSignalsResponse?.alerts_sent, "—")}
            </div>
            <div>
              monitor-exits: checked={firstDefined(monitorSignalsResponse?.checked, "—")}, alerts_sent={firstDefined(monitorSignalsResponse?.alerts_sent, "—")}
            </div>
          </div>
        )}

        {signalPerfError ? (
          <div style={{ marginTop: 10, color: "#fca5a5", fontSize: 13 }}>{signalPerfError}</div>
        ) : null}

        <div style={{ marginTop: 12, display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          <div style={{ border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>Strategies shown</div>
            <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700 }}>{firstDefined(signalSummary?.total_strategies, signalRows.length, "—")}</div>
          </div>
          <div style={{ border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>Total signals</div>
            <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700 }}>{firstDefined(signalSummary?.total_signals, "—")}</div>
          </div>
          <div style={{ border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>Total settled</div>
            <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700 }}>{firstDefined(signalSummary?.total_settled, "—")}</div>
          </div>
          <div style={{ border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>Weighted hit rate</div>
            <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700, color: roiColor(signalSummary?.weighted_hit_rate_pct) }}>
              {fmtPct(signalSummary?.weighted_hit_rate_pct)}
            </div>
          </div>
          <div style={{ border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>Closed position P/L</div>
            <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700, color: roiColor(signalSummary?.total_closed_position_pl) }}>
              {fmtSignedNum(signalSummary?.total_closed_position_pl, 2)}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {[
                  "Strategy",
                  "Signals total",
                  "Signals settled",
                  "Won",
                  "Lost",
                  "Signal hit rate %",
                  "Avg edge %",
                  "Positions logged",
                  "Open",
                  "Closed",
                  "Positions won",
                  "Positions lost",
                  "Positions ROI %",
                  "Position P/L units",
                ].map((h) => (
                  <th key={h} style={{ textAlign: h === "Strategy" ? "left" : "right", padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.3)", color: "#cbd5e1" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signalPerfLoading
                ? [...Array(5)].map((_, idx) => (
                    <tr key={`sig-skel-${idx}`}>
                      <td colSpan={14} style={{ padding: "10px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)", color: "#64748b" }}>
                        Loading signals performance…
                      </td>
                    </tr>
                  ))
                : signalRows.map((row, idx) => {
                    const signalHit = firstDefined(row.signal_hit_rate_pct, row.hit_rate_pct, row.signals_hit_rate_pct);
                    const avgEdge = firstDefined(row.avg_edge_pct, row.average_edge_pct, row.avg_edge);
                    const posRoi = firstDefined(row.positions_roi_pct, row.position_roi_pct, row.roi_pct_positions);
                    const posPl = firstDefined(row.position_pl_units, row.positions_pl_units, row.position_pl);
                    const strategyLabel = row.strategy || row.strategy_key || row.key || `Strategy ${idx + 1}`;
                    return (
                      <tr key={`${strategyLabel}-${idx}`}>
                        <td style={{ padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{strategyLabel}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.signals_total, row.total_signals, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.signals_settled, row.settled, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.won, row.signals_won, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.lost, row.signals_lost, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(signalHit)}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{fmtPct(avgEdge)}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.positions_logged, row.positions_total, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.open, row.positions_open, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.closed, row.positions_closed, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.positions_won, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{firstDefined(row.positions_lost, "—")}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)", color: roiColor(posRoi) }}>{fmtPct(posRoi)}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)", color: roiColor(posPl) }}>{fmtSignedNum(posPl, 2)}</td>
                      </tr>
                    );
                  })}
              {!signalPerfLoading && !signalRows.length ? (
                <tr>
                  <td colSpan={14} style={{ padding: 10, color: "#94a3b8" }}>No signal performance rows returned.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Admin Actions</h3>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Recalibrated: {recalUpdatedAt ? recalUpdatedAt.toLocaleTimeString() : "—"} • Recomputed: {recomputeUpdatedAt ? recomputeUpdatedAt.toLocaleTimeString() : "—"}
          </div>
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={doRecalibrate}
            disabled={recalLoading}
            style={{ padding: "8px 12px", borderRadius: 8, cursor: recalLoading ? "default" : "pointer" }}
          >
            {recalLoading ? "Recalibrating…" : "Recalibrate Now"}
          </button>
          <button
            type="button"
            onClick={doRecompute}
            disabled={recomputeLoading}
            style={{ padding: "8px 12px", borderRadius: 8, cursor: recomputeLoading ? "default" : "pointer" }}
          >
            {recomputeLoading ? "Recomputing…" : "Recompute Edges"}
          </button>
          <button
            type="button"
            onClick={() => copyJson({ recalibration: recalResponse, recompute: recomputeResponse })}
            style={{ padding: "8px 12px", borderRadius: 8 }}
          >
            Copy action JSON
          </button>
        </div>

        {recalError ? <div style={{ marginTop: 10, color: "#fca5a5", fontSize: 13 }}>{recalError}</div> : null}
        {recomputeError ? <div style={{ marginTop: 6, color: "#fca5a5", fontSize: 13 }}>{recomputeError}</div> : null}

        <div style={{ marginTop: 12, border: "1px solid rgba(148,163,184,0.25)", borderRadius: 10, padding: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 700 }}>Recalibration results</div>
            <button
              type="button"
              onClick={() => copyJson(recalResponse)}
              style={{ padding: "4px 8px", borderRadius: 8 }}
            >
              Copy JSON
            </button>
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: "#cbd5e1" }}>
            trained_ok: {firstDefined(recalSummary.trained_ok, recalResponse?.trained_ok, "—")} • failed: {firstDefined(recalSummary.failed, recalResponse?.failed, "—")}
          </div>
          {recalResponse?.message ? (
            <div style={{ marginTop: 6, color: "#fbbf24", fontSize: 13 }}>{recalResponse.message}</div>
          ) : null}

          <div style={{ marginTop: 10, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Market", "OK", "n_train", "alpha", "beta", "logloss"].map((h) => (
                    <th key={h} style={{ textAlign: h === "Market" ? "left" : "right", padding: "6px", borderBottom: "1px solid rgba(148,163,184,0.25)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recalResults.map((r, idx) => (
                  <tr key={`${r.market || "m"}-${idx}`}>
                    <td style={{ padding: "6px", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>{formatMarketName(r.market || "—")}</td>
                    <td style={{ padding: "6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.15)", color: r.ok ? "#34d399" : "#f87171" }}>
                      {String(!!r.ok)}
                    </td>
                    <td style={{ padding: "6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>{firstDefined(r.n_train, r.samples, "—")}</td>
                    <td style={{ padding: "6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>{fmtNum(r.alpha, 4)}</td>
                    <td style={{ padding: "6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>{fmtNum(r.beta, 4)}</td>
                    <td style={{ padding: "6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>{fmtNum(r.logloss, 4)}</td>
                  </tr>
                ))}
                {!recalResults.length ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 8, color: "#94a3b8" }}>No recalibration results yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
